// One pass over the dropped files, before the form: each file's waveform (so a
// blank export looks blank) and the tempo.
//
// The tempo is heard on the stems SUMMED — the project as it plays, where the
// beat is strongest — and on any drum-named stem alone; the most confident of
// those wins. One stem rarely carries the pulse by itself: a pad, a vocal, a
// bass with long notes all read as no tempo, or the wrong one.

import { decodeAudio, resampleAudio } from "@shared/audio/master-mix.js";
import { audioFeatures, type AudioFeatures } from "@web-shared/lib/tags/audio-features";
import { keyOfAudio } from "@web-shared/lib/tags/audio-key";
import type { KeyEstimate } from "@web-shared/lib/tags/key-profiles";
import { detectTempo, type TempoEstimate } from "@web-shared/lib/warp";

import type { IntakeFile } from "./intake";
import { BEAT_HINT } from "./tempo";

/** See tempo.ts: 22,050 Hz is the lowest rate that rounds every tempo right. */
export const LISTEN_RATE = 22050;
/** Per-file reading (features, own tempo) stops once this much time has gone:
 * suggestions are a courtesy, the upload is the point. */
const FEATURES_BUDGET_MS = 4000;
/** Past this many files, or this much audio, only the names are read. */
const FEATURES_MAX_FILES = 40;
const FEATURES_MAX_SECONDS = 20 * 60;
/** How much of the mix the tempo is heard on. */
const LISTEN_SECONDS = 45;
/** Columns in a track row's waveform. */
export const WAVE_BUCKETS = 160;

export interface Heard {
  /** The best tempo heard, confident or not; null when nothing had a pulse. */
  tempo: TempoEstimate | null;
  /** Per file, in order: interleaved min/max per bucket, at the file's own level (not normalised). */
  waves: Array<Float32Array | null>;
  /** Per file, in order: what the sound is like, for suggested tags. Null when
   * the file would not decode, or the budget ran out first. */
  features: Array<AudioFeatures | null>;
  /** Per file, in order: the file's OWN tempo (for a loop whose name and
   * length say nothing). Null for a one-shot or past the budget. */
  tempos: Array<TempoEstimate | null>;
  /** Per file, in order: the key heard in it, when it has one and there was
   * time. Null for drums, noise, short hits, or past the budget. */
  keys: Array<KeyEstimate | null>;
}

/** Mono at the listening rate. The browser's decoder first; the pipeline's own reader for what it can't (AIFF in Chrome). */
export async function monoAt(file: IntakeFile): Promise<Float32Array> {
  const bytes = await file.blob.arrayBuffer();
  try {
    const buf = await new OfflineAudioContext(1, 1, LISTEN_RATE).decodeAudioData(bytes.slice(0));
    return downmix(Array.from({ length: buf.numberOfChannels }, (_, c) => buf.getChannelData(c)), buf.length);
  } catch {
    const decoded = decodeAudio(new Uint8Array(bytes));
    if (!decoded) throw new Error(`Can't read ${file.name}`);
    const a = resampleAudio(decoded, LISTEN_RATE);
    const out = new Float32Array(a.frames);
    for (let f = 0; f < a.frames; f++) {
      let v = 0;
      for (let c = 0; c < a.channels; c++) v += a.samples[f * a.channels + c]!;
      out[f] = v / a.channels;
    }
    return out;
  }
}

export function downmix(channels: Float32Array[], frames: number): Float32Array {
  if (channels.length === 1) return channels[0]!;
  const out = new Float32Array(frames);
  for (const ch of channels) for (let i = 0; i < frames; i++) out[i]! += ch[i]! / channels.length;
  return out;
}

/** Interleaved min/max per bucket across the whole signal. Silence is all zeros. */
export function envelopeOf(mono: Float32Array, buckets: number = WAVE_BUCKETS): Float32Array {
  const out = new Float32Array(buckets * 2);
  if (mono.length === 0) return out;
  const per = mono.length / buckets;
  for (let b = 0; b < buckets; b++) {
    const from = Math.floor(b * per);
    const to = Math.max(from + 1, Math.floor((b + 1) * per));
    let lo = 0;
    let hi = 0;
    for (let i = from; i < to && i < mono.length; i++) {
      const v = mono[i]!;
      if (v < lo) lo = v;
      if (v > hi) hi = v;
    }
    out[b * 2] = lo;
    out[b * 2 + 1] = hi;
  }
  return out;
}

/** The most confident of some estimates, however low. */
export function mostConfident(estimates: Array<TempoEstimate | null>): TempoEstimate | null {
  let best: TempoEstimate | null = null;
  for (const e of estimates) if (e && (!best || e.confidence > best.confidence)) best = e;
  return best;
}

function tempoOf(mono: Float32Array): TempoEstimate | null {
  return detectTempo({ sampleRate: LISTEN_RATE, numberOfChannels: 1, length: mono.length, getChannelData: () => mono });
}

/** Listen to every file once, in order. A file that won't decode gets no waveform and stays out of the mix. */
export async function listenTo(
  files: IntakeFile[],
  onProgress?: (done: number, total: number) => void,
  opts: { features?: boolean; now?: () => number } = {},
): Promise<Heard> {
  const mixFrames = LISTEN_SECONDS * LISTEN_RATE;
  const mix = new Float32Array(mixFrames);
  let mixed = 0;
  let longest = 0;
  const waves: Array<Float32Array | null> = [];
  const features: Array<AudioFeatures | null> = [];
  const tempos: Array<TempoEstimate | null> = [];
  const keys: Array<KeyEstimate | null> = [];
  const estimates: Array<TempoEstimate | null> = [];
  const now = opts.now ?? (() => Date.now());
  const startedAt = now();
  let readFeatures = opts.features === true && files.length <= FEATURES_MAX_FILES;
  let secondsRead = 0;
  for (const [i, file] of files.entries()) {
    onProgress?.(i, files.length);
    try {
      const mono = await monoAt(file);
      waves.push(envelopeOf(mono));
      const n = Math.min(mixFrames, mono.length);
      for (let f = 0; f < n; f++) mix[f]! += mono[f]!;
      mixed++;
      longest = Math.max(longest, n);
      if (BEAT_HINT.test(file.name)) estimates.push(tempoOf(mono.subarray(0, mixFrames)));
      // What this one sounds like, while there is time for it. Each file on
      // its own: a reading that throws costs that file its tags, not the rest.
      secondsRead += mono.length / LISTEN_RATE;
      if (readFeatures && (now() - startedAt > FEATURES_BUDGET_MS || secondsRead > FEATURES_MAX_SECONDS)) readFeatures = false;
      let f: AudioFeatures | null = null;
      let t: TempoEstimate | null = null;
      let k: KeyEstimate | null = null;
      if (readFeatures) {
        try {
          f = audioFeatures(mono, LISTEN_RATE);
          if (mono.length >= LISTEN_RATE * 1.5) t = tempoOf(mono.subarray(0, mixFrames));
          // A key, for what could have one: half a second of sound at least.
          if (mono.length >= LISTEN_RATE * 0.5) k = keyOfAudio(mono, LISTEN_RATE);
        } catch {
          f = null;
          t = null;
          k = null;
        }
      }
      features.push(f);
      tempos.push(t);
      keys.push(k);
    } catch {
      waves.push(null);
      features.push(null);
      tempos.push(null);
      keys.push(null);
    }
  }
  if (mixed > 1) estimates.push(tempoOf(mix.subarray(0, longest)));
  else if (mixed === 1 && estimates.length === 0) estimates.push(tempoOf(mix.subarray(0, longest)));
  return { tempo: mostConfident(estimates), waves, features, tempos, keys };
}
